import { CellClassParams } from '@ag-grid-community/all-modules';

// Constants
//Add to this if there are othjer which are breaking the colour logic
const FIELD_TYPES = ['Grade', 'Entries', 'Score', 'AvgPA', 'Grades'] as const;
//Alp's grade boundaries
const GRADE_BOUNDARIES = {
	RED: { min: 1, max: 3 },
	BLUE: { min: 7, max: 9 },
} as const;
//CSS classes for the grade cells
const CSS_CLASSES = {
	NO_DATA: 'alps-grade-no-data',
	RED: 'alps-grade-red',
	BLUE: 'alps-grade-blue',
	TRANSPARENT: 'alps-grade-transparent',
} as const;

/**
 * CSS styling for AG Grid cells
 * Handles cases where the type is repeated in the field name (e.g., GradeGrade, GradeEntries, EntriesGrade, etc.)
 * @param params - AG Grid cell parameters
 * @returns CSS class name for styling
 */
const AlpsGradeClass = (params: CellClassParams): string => {
	//pull out the field name
	const field = params.colDef.field ?? '';

	// Parse field name using regex
	const fieldMatch = parseFieldName(field);
	if (!fieldMatch) return '';

	// Extract data values
	const { grade, entries, score } = extractDataValues(params.data, fieldMatch);

	// Determine CSS class based on data values
	return determineCssClass(grade, entries, score);
};

/**
 * Parses the field name to extract key components
 * @param field - The field name to parse
 * @returns Parsed field components or null if no match
 */
const parseFieldName = (field: string) => {
	//@ts-ignore
	const sortedTypes = [...FIELD_TYPES].sort((a, b) => b.length - a.length);
	//From the data, this will then link to the col defs
	const regex = new RegExp(`^gp(.*?)(${sortedTypes.join('|')})(.*)$`);
	return regex.exec(field);
};

/**
 * Detects if the field contains repeated or combined type patterns
 * @param field - The field name to check
 * @returns True if field contains weird key patterns
 */
const hasWeirdKeyPattern = (field: string): boolean => {
	return FIELD_TYPES.some((type1) =>
		FIELD_TYPES.some((type2) =>
			type1 !== type2 ? field.includes(type1 + type2) : field.includes(type1 + type1)
		)
	);
};

/**
 * Extracts grade, entries, and score values from the data
 * @param data - Row data object
 * @param fieldMatch - Parsed field components
 * @returns Object containing grade, entries, and score values
 */
const extractDataValues = (data: any, fieldMatch: RegExpExecArray) => {
	const [, key, type, suffix] = fieldMatch;
	//does the get contain something like GradeGrade or GradeEntries
	const isWeirdKey = hasWeirdKeyPattern(fieldMatch[0]);
	//if it does, then we need to add the type to the key
	const finalKey = isWeirdKey ? key + type : key;
	//if there is a suffix, then we need to add it to the key
	const finalSuffix = suffix || '';
	//this will then link to the col defs
	const getPropertyValue = (propertyType: string) => {
		const propertyKey = isWeirdKey
			? `gp${finalKey}${propertyType}`
			: `gp${finalKey}${propertyType}${finalSuffix}`;
		return data[propertyKey];
	};

	//return the correct values
	return {
		grade: getPropertyValue('Grade'),
		entries: getPropertyValue('Entries'),
		score: getPropertyValue('Score'),
	};
};

/**
 * Determines the appropriate CSS class based on data values
 * @param grade - Grade value
 * @param entries - Entries value
 * @param score - Score value
 * @returns CSS class name
 */
const determineCssClass = (grade: any, entries: any, score: any): string => {
	// Check if no data is available
	if (!entries && !grade && !score) {
		return CSS_CLASSES.NO_DATA;
	}

	// Check grade boundaries for color coding
	const isRedGrade = grade >= GRADE_BOUNDARIES.RED.min && grade <= GRADE_BOUNDARIES.RED.max;
	const isBlueGrade = grade >= GRADE_BOUNDARIES.BLUE.min && grade <= GRADE_BOUNDARIES.BLUE.max;

	if (isRedGrade) return CSS_CLASSES.RED;
	if (isBlueGrade) return CSS_CLASSES.BLUE;

	return CSS_CLASSES.TRANSPARENT;
};

export default AlpsGradeClass;
